import { Category } from '@/interfaces/catalog/Category'
import { findCategoryById } from '@/lib/catalog/categoryUtils'
import { cn } from '@/lib/utils'

interface CategoryNavigationProps {
  /** Strom kategorií (kořeny s children) */
  categories: Category[]
  /** Vybraná cesta od kořene, poslední id je aktivní kategorie */
  path: string[]
  onPathChange: (path: string[]) => void
  /** Kategorie bez produktů (ani v potomcích) se nezobrazí */
  isVisible: (category: Category) => boolean
}

/** trail = vybraný předek: jen jemná plocha, plnou pilulku má jen nejhlubší výběr */
const Chip = ({ active, trail = false, onClick, children }: { active: boolean; trail?: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'inline-flex h-11 shrink-0 items-center rounded-full px-4 text-sm font-medium whitespace-nowrap transition-colors select-none',
      'focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
      active
        ? trail
          ? 'bg-muted text-foreground hover:bg-neutral-bg'
          : 'bg-foreground text-background'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    )}
  >
    {children}
  </button>
)

/**
 * Kategorie jako řady pilulek. První řada jsou kořeny, každá další řada
 * jsou podkategorie vybrané kategorie. "Vše" vrátí o úroveň výš.
 */
export function CategoryNavigation({ categories, path, onPathChange, isVisible }: CategoryNavigationProps) {
  const levels: { options: Category[]; active: string | undefined; parent: Category | null }[] = []

  for (let depth = 0; depth <= path.length; depth++) {
    const parent = depth === 0 ? null : findCategoryById(categories, path[depth - 1]) ?? null
    const options = (depth === 0 ? categories.filter((c) => !c.parentId) : parent?.children ?? []).filter(isVisible)
    if (options.length === 0) break
    levels.push({ options, active: path[depth], parent })
  }

  if (levels.length === 0) return null

  return (
    <div className="min-w-0 space-y-1">
      {levels.map((level, depth) => (
        <div key={depth} className="-mx-1 flex gap-1 overflow-x-auto px-1 py-0.5 [scrollbar-width:none]">
          <Chip active={!level.active} onClick={() => onPathChange(path.slice(0, depth))}>
            {depth === 0 ? 'Vše' : `Vše v ${level.parent?.name ?? ''}`}
          </Chip>
          {level.options.map((category) => (
            <Chip
              key={category.id}
              active={level.active === category.id}
              trail={depth < levels.length - 1}
              onClick={() => onPathChange([...path.slice(0, depth), category.id])}
            >
              {category.name}
            </Chip>
          ))}
        </div>
      ))}
    </div>
  )
}
